import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layer } from '@fluentui/react';
import {
  BackgroundJobs,
  BackgroundJobsEventCallback,
  BackgroundJobsEventType,
  BackgroundJobsState,
  BackgroundJobStatus,
  IAddBackgroundJobParams,
  IBackgroundJobItem,
  IBackgroundJobsContext,
  IBackgroundJobsEvent,
  IBackgroundJobsMenuTarget
} from './model';
import TransientOverlays from './TransientOverlays';

/** Provides a BackgroundJobs context and hosts DOM overlays. */
export default function BackgroundJobsProvider({ children }: { children: ReactNode }) {
  const eventListeners = useRef(new Set<BackgroundJobsEventCallback>());
  const [jobs, setJobs] = useState<IBackgroundJobItem[]>([]);

  const dispatchEvent = useCallback((event: IBackgroundJobsEvent) => {
    for (const listener of eventListeners.current) {
      listener(event);
    }
  }, []);

  const addJob = useCallback(
    (params: IAddBackgroundJobParams) => {
      dispatchEvent({
        type: BackgroundJobsEventType.Added,
        params
      });

      setJobs((jobs) => [
        {
          job: params.job,
          isAwaitingOverlayEnd: !!params.initiatingElement,
          lastUpdatedTime: Date.now()
        },
        ...jobs
      ]);
    },
    [dispatchEvent]
  );

  const [menuTargets, setMenuTargets] = useState(new Set<IBackgroundJobsMenuTarget>());

  const menuTargetsRef = useRef(menuTargets);
  menuTargetsRef.current = menuTargets;

  const context: IBackgroundJobsContext = useMemo(
    () => ({
      add: addJob,
      remove(job) {
        setJobs((jobs) => jobs.filter((item) => item.job !== job));
      },
      removeCompleted() {
        setJobs((jobs) =>
          jobs.filter((item) => item.job.getProgress().status === BackgroundJobStatus.Pending)
        );
      },
      addEventListener: (listener) => eventListeners.current.add(listener),
      removeEventListener: (listener) => eventListeners.current.delete(listener),
      signalOverlayEnded(job, ephemeralSuccess) {
        setJobs((jobs) => {
          if (ephemeralSuccess) {
            // this job is ephemeral and succeeded during overlay: remove it
            return jobs.filter((item) => item.job !== job);
          }
          return jobs.map((item) => {
            if (item.job === job) {
              return { ...item, isAwaitingOverlayEnd: false };
            }
            return item;
          });
        });

        dispatchEvent({
          type: BackgroundJobsEventType.OverlayEnded,
          job,
          ephemeralSucceededDuringOverlay: ephemeralSuccess
        });
      },
      getMenuTargets() {
        // this must NOT depend on menuTargets because we'd get an infinite update loop otherwise
        return menuTargetsRef.current;
      },
      addMenuTarget: (target) =>
        setMenuTargets((targets) => {
          const newTargets = new Set(targets);
          newTargets.add(target);
          return newTargets;
        }),
      removeMenuTarget: (target) =>
        setMenuTargets((targets) => {
          const newTargets = new Set(targets);
          newTargets.delete(target);
          return newTargets;
        })
    }),
    [addJob, dispatchEvent]
  );

  // listen to job updates and dispatch Updated event
  useEffect(() => {
    const detach: (() => void)[] = [];

    for (const { job } of jobs) {
      const onUpdate = () => {
        dispatchEvent({
          type: BackgroundJobsEventType.Updated,
          job
        });
        setJobs((jobs) =>
          jobs.map((item) => {
            if (item.job === job) {
              return { ...item, lastUpdatedTime: Date.now() };
            }
            return item;
          })
        );
      };
      job.addProgressUpdateListener(onUpdate);
      detach.push(() => {
        job.removeProgressUpdateListener(onUpdate);
      });
    }

    return () => {
      for (const f of detach) f();
    };
  }, [dispatchEvent, jobs]);

  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;

  // warn when attempting to unload with pending jobs
  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      const jobs = jobsRef.current;
      if (jobs.some((item) => item.job.getProgress().status === BackgroundJobStatus.Pending)) {
        dispatchEvent({ type: BackgroundJobsEventType.AttemptedClose });
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dispatchEvent]);

  return (
    <BackgroundJobs.Provider value={context}>
      <BackgroundJobsState.Provider value={jobs}>
        {children}
        <div className="background-jobs-host-layer-container" style={{ display: 'none' }}>
          <Layer styles={{ root: { zIndex: 'calc(infinity)' } }}>
            <TransientOverlays />
          </Layer>
        </div>
      </BackgroundJobsState.Provider>
    </BackgroundJobs.Provider>
  );
}
